import { shallowRef, watch, type Ref, type ShallowRef } from 'vue'
import { defaultGetItemId } from './gpx-select-option-utils'

export type UseGpxSelectResolvedOptionOptions<T> = {
  modelValue?: Ref<unknown>
  getItemId?: (item: T) => string | number
  resolveSelected?: (value: unknown) => Promise<T | null>
  findInItems: (value: unknown) => T | undefined | null
  clearWhenFoundInItems?: boolean
}

export function useGpxSelectResolvedOption<T>(
  options: UseGpxSelectResolvedOptionOptions<T>,
) {
  const getItemId = options.getItemId ?? defaultGetItemId<T>
  const resolvedSelected = shallowRef<T | null>(null) as ShallowRef<T | null>

  watch(
    () => options.modelValue?.value,
    async (value) => {
      if (value == null || value === '') {
        resolvedSelected.value = null
        return
      }
      const item = options.findInItems(value)
      if (item != null) {
        resolvedSelected.value = options.clearWhenFoundInItems ? null : item
        return
      }
      if (
        resolvedSelected.value != null &&
        getItemId(resolvedSelected.value) === value
      ) {
        return
      }
      if (!options.resolveSelected) {
        return
      }
      const resolved = await options.resolveSelected(value)
      if (resolved) {
        resolvedSelected.value = resolved
      }
    },
    { immediate: true },
  )

  function cacheSelected(item: T | null) {
    resolvedSelected.value = item
  }

  return { resolvedSelected, getItemId, cacheSelected }
}
